import type { AnnotationFormData } from '@/components/annotation';

export interface SessionSong {
  id: string;
  source: 'suno' | 'mureka' | 'sonauto' | 'udio' | 'human';
  title: string;
  audioUrl: string;
  duration: number;
}

export interface AnnotationSessionState {
  sessionId: string;
  participantId: string;
  currentSongIndex: number;
  totalSongs: number;
  completedAnnotations: number;
  estimatedTimeRemaining: number;
  currentSong?: SessionSong;
  isComplete: boolean;
}

export interface SessionConfig {
  totalSongs?: number;
  balanceAcrossSources?: boolean;
  excludeAnnotatedSongs?: boolean;
  minDuration?: number;
  maxDuration?: number;
}

export interface CreateSessionRequest {
  participantId: string;
  sessionConfig?: SessionConfig;
}

export interface CreateSessionResponse {
  sessionId: string;
  sessionState: AnnotationSessionState;
  message: string;
}

export interface GetSessionResponse {
  sessionState: AnnotationSessionState;
}

export interface GetSessionSongsResponse {
  sessionSongs: SessionSong[];
  totalSongs: number;
  currentSongIndex: number;
}

export interface SubmitAnnotationRequest {
  songId: string;
  songSource: string;
  authenticityAssessment: 'real' | 'ai-generated' | 'uncertain';
  familiarityLevel: 'know' | 'familiar' | 'never' | 'uncertain';
  aestheticQuality: number;
  playlistLikelihood: number;
  musicalCreativity: number;
  productionQuality: number;
  emotionalEngagement: number;
  aiAspects?: string[];
  moodTags: string[];
  aestheticComment?: string;
  songDescription?: string;
  annotationDurationMs: number;
  songListeningTimeMs: number;
  songDurationMs?: number;
  listeningPercentage?: number;
}

export interface SubmitAnnotationResponse {
  annotationId: string;
  isComplete: boolean;
  nextSong?: SessionSong;
  sessionState?: AnnotationSessionState;
  message: string;
}

export interface UpdateSessionRequest {
  currentSongIndex?: number;
  complete?: boolean;
}

class AnnotationSessionService {
  private baseUrl = '/api/annotation-sessions';

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = localStorage.getItem('auth-token');
    
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * Create a new annotation session
   */
  async createSession(request: CreateSessionRequest): Promise<CreateSessionResponse> {
    return this.makeRequest<CreateSessionResponse>('', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Get annotation session state
   */
  async getSession(sessionId: string): Promise<GetSessionResponse> {
    return this.makeRequest<GetSessionResponse>(`/${sessionId}`);
  }

  /**
   * Update session progress or complete session
   */
  async updateSession(sessionId: string, updates: UpdateSessionRequest): Promise<GetSessionResponse> {
    return this.makeRequest<GetSessionResponse>(`/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Get session songs list
   */
  async getSessionSongs(sessionId: string): Promise<GetSessionSongsResponse> {
    return this.makeRequest<GetSessionSongsResponse>(`/${sessionId}/songs`);
  }

  /**
   * Submit annotation for current song
   */
  async submitAnnotation(
    sessionId: string, 
    annotation: SubmitAnnotationRequest
  ): Promise<SubmitAnnotationResponse> {
    return this.makeRequest<SubmitAnnotationResponse>(`/${sessionId}/annotations`, {
      method: 'POST',
      body: JSON.stringify(annotation),
    });
  }

  /**
   * Convert AnnotationFormData to SubmitAnnotationRequest
   */
  convertAnnotationData(
    formData: AnnotationFormData,
    songId: string,
    songSource: string,
    duration: number,
    listeningTime: number,
    songDuration?: number,
    listeningPercentage?: number
  ): SubmitAnnotationRequest {
    return {
      songId,
      songSource,
      authenticityAssessment: formData.authenticityAssessment,
      familiarityLevel: formData.familiarityLevel,
      aestheticQuality: formData.aestheticQuality,
      playlistLikelihood: formData.playlistLikelihood,
      musicalCreativity: formData.musicalCreativity,
      productionQuality: formData.productionQuality,
      emotionalEngagement: formData.emotionalEngagement,
      aiAspects: formData.aiAspects,
      moodTags: formData.moodTags,
      aestheticComment: formData.aestheticComment,
      songDescription: formData.songDescription,
      annotationDurationMs: duration,
      songListeningTimeMs: listeningTime,
      songDurationMs: songDuration,
      listeningPercentage: listeningPercentage
    };
  }

  /**
   * Complete session
   */
  async completeSession(sessionId: string): Promise<GetSessionResponse> {
    return this.updateSession(sessionId, { complete: true });
  }

  /**
   * Move to next song
   */
  async nextSong(sessionId: string, currentIndex: number): Promise<GetSessionResponse> {
    return this.updateSession(sessionId, { currentSongIndex: currentIndex + 1 });
  }

  /**
   * Move to previous song
   */
  async previousSong(sessionId: string, currentIndex: number): Promise<GetSessionResponse> {
    if (currentIndex > 0) {
      return this.updateSession(sessionId, { currentSongIndex: currentIndex - 1 });
    }
    throw new Error('Already at first song');
  }
}

export const annotationSessionService = new AnnotationSessionService();